<template>
  <!-- 分组 鼠标放上去的样式 -->
  <div class="mgx_key_group_hover" v-if="isGroupHovered"></div>

  <!-- 不可选择的按键 -->
  <div class="mgx_key_type disabled" v-if="magnetKeyType">
    {{ magnetKeyType }}
  </div>

  <template v-else-if="hasMagnetData">
    <div
      class="mgx_data"
      :class="{
        has_quick_reset: quickResetNum !== null,
        is_selected: isSelectedMagnetKey,
      }"
    >
      <div class="mgx_key_group" :style="{ backgroundColor: activeKeyGroupColor || 'transparent' }"></div>
      <span class="mgx_metric mgx_metric_primary" v-if="magnetPressText !== null">{{ magnetPressText }}</span>
      <span class="mgx_metric mgx_metric_secondary" v-if="quickResetText !== null">{{ quickResetText }}</span>
    </div>
  </template>
</template>
<script setup lang="ts">
import { KK } from '@/define/define'
import { KeyGroupInfo, KeyGroupInfoMap } from '@/define/key_group'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import i18n from '@/languages/i18n'
import { setData } from '@/service/driver/data'
import { magnetSetKeyGroup } from '@/service/driver/magnet_key_group'
import { magnetSet } from '@/service/driver/magnet_set'
import { convertTravel, getColorRGBA } from '@/tool/utility'
import { MagnetKeyType, MagnetSeniorType } from '@libs/types/enum'
import { computed } from 'vue'
import { IMCSwitchState, isFE87V2IMCNH } from '../mgx_set/IMC_switch.vue'
const keyGroupInfoMap = KeyGroupInfoMap()
const props = defineProps<{
  kk: number
}>()
const setMagnetTexts = {
  [MagnetKeyType.Macro]: 'Macro',
  [MagnetKeyType.DKS]: 'DKS',
  [MagnetKeyType.MT]: 'MT',
  [MagnetKeyType.TGL]: 'TGL',
  [MagnetKeyType.MPT]: 'MPT',
  [MagnetKeyType.END]: 'END',
  [MagnetKeyType.KME]: 'KME',
  [MagnetKeyType.RS]: 'RS',
}
const isDKS = (kk: KK): boolean => {
  return setData.getData().Layout.Magenet[kk] && setData.getData().Layout.Magenet[kk].KeyType == MagnetSeniorType.SeniorKeyTypeDKS
}

const keyData = computed(() => {
  return magnetSet.list[props.kk] ?? null
})

const isSelectedMagnetKey = computed(() => {
  return magnetSet.selectedKeySet.value.has(props.kk)
})

const isGroupHovered = computed(() => {
  return magnetSetKeyGroup.hoverKeySet.value.has(props.kk) && magnetKeyType.value == null && !isSelectedMagnetKey.value
})

const hasMagnetData = computed(() => {
  return keyData.value != null && IMCSwitchState.value === false && isFE87V2IMCNH() === false
})

const magnetKeyType = computed(() => {
  if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
    return null
  }
  if (specialCheckCurrent(speciaBehiver.DevicesOfWitmod)) {
    if (isDKS(props.kk)) return 'DKS'
    return null
  }
  const data = keyData.value
  if (!data) return null

  // 支持kme 的厂商和royuan的厂商，设置了kme 也可以设置rt 行程
  if (specialCheckCurrent(speciaBehiver.SupportKME) || specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
    if (data && data.KeyType != MagnetKeyType.KME) {
      return setMagnetTexts[data.KeyType]
    }
    return null
  }

  return setMagnetTexts[data.KeyType]
})

// 查找当前按键的PressNum
const magnetPressNum = computed(() => {
  if (keyData.value && keyData.value.PressNum > 0) {
    return keyData.value.PressNum
  }
  return null
})

const quickResetNum = computed(() => {
  if (keyData.value && keyData.value.QuickTriggerReleaseNum > 0) {
    return keyData.value.QuickTriggerReleaseNum
  }
  return null
})

const quickTriggerPressNum = computed(() => {
  if (keyData.value && keyData.value.QuickTriggerPressNum > 0) {
    return keyData.value.QuickTriggerPressNum
  }
  return null
})

const magnetPressText = computed(() => {
  return magnetPressNum.value === null ? null : formatKeycapTravel(magnetPressNum.value)
})

const quickResetText = computed(() => {
  return quickResetNum.value === null ? null : formatKeycapTravel(quickResetNum.value)
})

const formatKeycapTravel = (num: number): string => {
  if (!Number.isFinite(num) || num <= 0) {
    return convertTravel(0)
  }

  return convertTravel(num)
}

// 查找非默认的按键分组颜色
const activeKeyGroupColor = computed(() => {
  let info: KeyGroupInfo | undefined
  for (const [keyGroup, kks] of magnetSetKeyGroup.state.keysGroup) {
    if (kks.includes(props.kk)) {
      if (keyGroup == magnetSetKeyGroup.state.defaultKeysGroup) {
        continue
      }
      info = keyGroupInfoMap.get(keyGroup)
      break
    }
  }
  if (info && info.color > 0) {
    return getColorRGBA(info.color, 0.6)
  }
  return null
})
</script>
<style lang="less">
.mgx_key_type {
  position: absolute;
  width: 100%;
  bottom: 0px;
  color: var(--color-gray-2);
  font-weight: bold;
  opacity: 0.8;
  font-size: 10px;
  padding: 2px auto;
  text-align: center;
}

.mgx_data {
  position: absolute;
  left: 3px;
  right: 3px;
  top: 3px;
  bottom: 2px;
  min-height: auto;
  z-index: 1;

  .mgx_key_group {
    position: absolute;
    bottom: -1px;
    left: 4px;
    right: 4px;
    height: 3px;
    min-width: 0;
    max-width: 50px;
    margin: 0 auto;
    border-radius: var(--radius-pill);
    opacity: 0.9;
  }

  .mgx_metric {
    position: absolute;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.35);
    white-space: nowrap;
    pointer-events: none;
  }

  .mgx_metric_primary {
    top: 0px;
    left: 2px;
    color: var(--brand-color-pure);
    min-width: unset;
    font-size: 9px;
    font-weight: 700;
    opacity: 0.5;
    letter-spacing: 0;

    &.solo {
      left: 2px;
      transform: none;
    }
  }

  .mgx_metric_secondary {
    bottom: 0px;
    right: 2px;
    color: #2b7fff;
    font-size: 9px;
    font-weight: 700;
    opacity: 0.5;
    letter-spacing: 0;
  }

  &.is_selected {
    .mgx_key_group {
      opacity: 0.52;
    }

    .mgx_metric_primary,
    .mgx_metric_secondary {
      color: var(--color-white);
      -webkit-text-fill-color: var(--color-white);
      opacity: 1;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    }

    .mgx_metric_secondary {
      opacity: 0.92;
    }
  }
}

.mgx_data ~ .texture {
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 100%;
  padding-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-dark);
  opacity: 0.86;
  z-index: 2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.1px;
}

.device_key .mgx_key_group_hover {
  background: rgba(255, 255, 255, 0.5);
}

/* todo: mgx 鼠标放上去的动画 */
.mgx_key_group_hover {
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-s);
}
</style>
